import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getSession } from 'next-auth/react';

export interface WishlistItem {
  id: string; // product id
  name: string;
  price: number;
  slug: string;
  image?: string | null;
}

interface WishlistStore {
  items: WishlistItem[];
  addItem: (item: WishlistItem) => void;
  removeItem: (id: string) => void;
  toggleItem: (item: WishlistItem) => void;
  isInWishlist: (id: string) => boolean;
  clearWishlist: () => void;
  syncFromServer: () => Promise<void>;
}

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: async (newItem) => {
        const currentItems = get().items;
        if (!currentItems.find((item) => item.id === newItem.id)) {
          set({ items: [...currentItems, newItem] });
          // Sayaç artır
          try {
            await fetch(`/api/products/${newItem.id}/stats`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "favorite" }),
            });
          } catch (e) { console.error("Favorite stat error"); }

          // Giriş yapmış kullanıcı için DB'ye kalıcı kaydet
          try {
            const session = await getSession();
            if (session?.user) {
              await fetch('/api/user/favorites', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: newItem.id }),
              });
            }
          } catch (e) { console.error("Favorite DB sync error"); }
        }
      },

      removeItem: async (id) => {
        set({
          items: get().items.filter((item) => item.id !== id),
        });
        // Sayaç azalt
        try {
          await fetch(`/api/products/${id}/stats`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "remove_favorite" }),
          });
        } catch (e) { console.error("Remove favorite stat error"); }

        // Giriş yapmış kullanıcı için DB'den kaldır
        try {
          const session = await getSession();
          if (session?.user) {
            await fetch(`/api/user/favorites?productId=${id}`, { method: 'DELETE' });
          }
        } catch (e) { console.error("Favorite DB sync error"); }
      },

      toggleItem: (item) => {
        if (get().isInWishlist(item.id)) {
          get().removeItem(item.id);
        } else {
          get().addItem(item);
        }
      },

      isInWishlist: (id) => {
        return get().items.some((item) => item.id === id);
      },

      clearWishlist: () => set({ items: [] }),

      // Giriş yapınca DB'deki favorileri yerel listeyle birleştir (cihazlar arası kalıcılık)
      syncFromServer: async () => {
        try {
          const res = await fetch('/api/user/favorites');
          if (!res.ok) return;
          const dbFavorites = await res.json();
          const dbItems: WishlistItem[] = dbFavorites.map((f: any) => ({
            id: f.product.id,
            name: f.product.nameTR,
            price: f.product.variants?.[0]?.price ?? 0,
            slug: f.product.slug,
            image: f.product.images ? JSON.parse(f.product.images)[0] : null,
          }));

          const currentItems = get().items;
          const merged = [...currentItems];
          for (const dbItem of dbItems) {
            if (!merged.find((i) => i.id === dbItem.id)) merged.push(dbItem);
          }
          set({ items: merged });

          // Yerelde olup DB'de olmayanları (başka cihazda eklenmemiş) DB'ye yaz
          for (const item of currentItems) {
            if (!dbItems.find((d) => d.id === item.id)) {
              fetch('/api/user/favorites', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: item.id }),
              }).catch(() => {});
            }
          }
        } catch (e) { console.error("Favorite sync error"); }
      },
    }),
    {
      name: 'my-parfum-wishlist',
    }
  )
);
